import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import FlexCentered from 'components/Common/FlexCentered';
import Loading from 'components/Loading';
import UsersContext from 'contexts/UsersContext';
import UsersSettingsContext from 'contexts/UsersSettingsContext';
import useIsSsoActive from 'screens/Settings/SsoConfigurations/Microsoft/hooks/useIsSsoActive';

import userTypeCheckers from '../SplashScreenProviders/UserAndAccountProviders/UsersProvider/userTypeCheckers';

const UsersSettingsProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { userId } = useParams<{
    userId?: string;
  }>();
  const { isSsoActive, isLoading: isLoadingConfig } = useIsSsoActive();
  const { allUsers, isLoading } = useContext(UsersContext);
  const [users, setUsers] = useState<
    (UserManagement.PendingUser | UserManagement.SignedUpUser)[]
  >([]);
  const [selectedUser, setSelectedUser] = useState<
    UserManagement.PendingUser | UserManagement.SignedUpUser | undefined
  >(undefined);

  useEffect(() => {
    const visibleUsers = allUsers
      .filter(userTypeCheckers.isSignedUpOrPendingUser)
      .filter((u) => !u.isDeleted);

    const signedUpUsers = visibleUsers
      .filter(userTypeCheckers.isSignedUpUser)
      .sort((a, b) => {
        return a.displayName
          .toLowerCase()
          .trim()
          .localeCompare(b.displayName.toLowerCase().trim());
      });

    const pendingUsers = visibleUsers
      .filter(userTypeCheckers.isPendingUser)
      .sort((a, b) => {
        return (a.email || '')
          .toLowerCase()
          .trim()
          .localeCompare((b.email || '').toLowerCase().trim());
      });

    setUsers([...signedUpUsers, ...pendingUsers]);
  }, [allUsers]);

  useEffect(() => {
    const maybeUser = users.find((u) => u.id === userId);
    setSelectedUser(maybeUser);
  }, [users, userId]);

  if (isLoadingConfig) {
    return (
      <FlexCentered style={{ height: '100%', width: '100%' }}>
        <Loading />
      </FlexCentered>
    );
  }

  return (
    <UsersSettingsContext.Provider
      value={{
        selectedUser,
        users,
        isLoading,
        isSsoAccount: isSsoActive,
        isLoadingIsSsoAccount: isLoadingConfig,
      }}
    >
      {children}
    </UsersSettingsContext.Provider>
  );
};

export default UsersSettingsProvider;
